import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Undo, Check, Plus, Trash2 } from 'lucide-react';
import { ProductSearchSelect } from '../components/ProductSearchSelect';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

export function ReturnStockPage() {
    const { t } = useLanguage();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // We need to know how many AVAILABLE units each product has
    const [inventoryStats, setInventoryStats] = useState({});

    // Dynamic Items State
    const [items, setItems] = useState([{ id: Date.now(), productId: '', quantity: '1', sellPrice: '' }]);
    const [notes, setNotes] = useState('');
    const [customerName, setCustomerName] = useState('Direct Sale');
    const [date, setDate] = useState(() => new Date().toISOString().split('T')[0]);

    // Confirm Dialog State
    const [confirmOpen, setConfirmOpen] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch product options for the dropdown
                const prodRes = await axios.get(`${API_URL}/products`, { headers });
                setProducts(prodRes.data.data.products);

                // Fetch how many SOLD/RETURNED etc items are traceable outside stock to allow returning them
                // For simplicity we query SOLD items to establish a maximum return quantity
                const invRes = await axios.get(`${API_URL}/inventory?status=SOLD&limit=5000`, { headers });

                // Group by productId
                const stats = {};
                invRes.data.data.units.forEach(unit => {
                    stats[unit.productId] = (stats[unit.productId] || 0) + 1;
                });
                setInventoryStats(stats);
            } catch (error) {
                console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const addItem = () => {
        setItems([...items, { id: Date.now(), productId: '', quantity: '1', sellPrice: '' }]);
    };

    const removeItem = (id) => {
        if (items.length === 1) return;
        setItems(items.filter(item => item.id !== id));
    };

    const updateItem = (id, field, value) => {
        setItems(items.map(item => {
            if (item.id === id) {
                const updatedItem = { ...item, [field]: value };
                // If product changed, optionally set a default sell price if we had standard pricing
                return updatedItem;
            }
            return item;
        }));
    };

    // Calculate totals
    const totalQuantity = items.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
    const totalRevenue = items.reduce((sum, item) => sum + ((parseFloat(item.sellPrice) || 0) * (parseInt(item.quantity) || 0)), 0);

    // Validation
    const isFormValid = items.length > 0 && items.every(item =>
        item.productId &&
        parseInt(item.quantity) > 0 &&
        parseFloat(item.sellPrice) >= 0 &&
        parseInt(item.quantity) <= (inventoryStats[item.productId] || 0)
    );

    const handleConfirm = () => {
        if (!isFormValid) return;
        setConfirmOpen(true);
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            const payloadItems = items.map(item => ({
                productId: item.productId,
                quantity: parseInt(item.quantity),
                sellPrice: parseFloat(item.sellPrice)
            }));

            await axios.post(`${API_URL}/inventory/return-stock`, {
                items: payloadItems,
                customerName,
                notes,
                date: new Date(date).toISOString()
            }, { headers });

            // Reset form
            setItems([{ id: Date.now(), productId: '', quantity: '1', sellPrice: '' }]);
            setNotes('');
            setDate(new Date().toISOString().split('T')[0]);
            setConfirmOpen(false);
            toast.success(t('successCreated') || 'Successfully returned stock!');

            // Refresh inventory stats
            const invRes = await axios.get(`${API_URL}/inventory?status=SOLD&limit=5000`, { headers });
            const stats = {};
            invRes.data.data.units.forEach(unit => {
                stats[unit.productId] = (stats[unit.productId] || 0) + 1;
            });
            setInventoryStats(stats);

        } catch (error) {
            console.error('Failed to sell stock:', error);
            toast.error(error.response?.data?.error || t('errorOccurred'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-6 max-w-4xl mx-auto">
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold flex items-center gap-2">
                            <Undo className="h-6 w-6 text-purple-600" />
                            Process Returns
                        </h1>
                        <p className="text-muted-foreground">Log items that have been returned by customers to make them available again in inventory.</p>
                    </div>
                </div>

                <Card>
                    <CardHeader>
                        <CardTitle>Return Stock</CardTitle>
                        <CardDescription>Select previously sold products and quantities to return.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">

                        <div className="space-y-4">
                            {items.map((item, index) => {
                                const available = inventoryStats[item.productId] || 0;
                                const isQtyError = item.productId && parseInt(item.quantity) > available;

                                return (
                                    <div key={item.id} className="flex items-start gap-4 p-4 border rounded-lg bg-gray-50/50">
                                        <div className="flex-1 grid grid-cols-12 gap-4">

                                            <div className="col-span-12 md:col-span-5 space-y-2">
                                                <ProductSearchSelect
                                                    products={products}
                                                    value={item.productId}
                                                    onChange={(val) => updateItem(item.id, 'productId', val)}
                                                    placeholder={t('selectProduct')}
                                                    label={t('product')}
                                                />
                                            </div>

                                            <div className="col-span-6 md:col-span-3 space-y-2">
                                                <Label>{t('quantity')}</Label>
                                                <Input
                                                    type="number"
                                                    min="1"
                                                    max={available}
                                                    value={item.quantity}
                                                    onChange={(e) => updateItem(item.id, 'quantity', e.target.value)}
                                                    className={isQtyError ? 'border-red-500' : ''}
                                                />
                                                {isQtyError && (
                                                    <p className="text-xs text-red-500 mt-1">
                                                        Max available: {available}
                                                    </p>
                                                )}
                                            </div>

                                            <div className="col-span-6 md:col-span-4 space-y-2">
                                                <Label>{t('sellPrice')} Paid (TND)</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={item.sellPrice}
                                                    onChange={(e) => updateItem(item.id, 'sellPrice', e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </div>

                                        </div>

                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => removeItem(item.id)}
                                            disabled={items.length === 1}
                                            className="mt-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                );
                            })}

                            <Button type="button" variant="outline" onClick={addItem} className="w-full border-dashed">
                                <Plus className="h-4 w-4 mr-2" />
                                {t('addAnotherProduct')}
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>{t('date') || 'Date'}</Label>
                                <Input
                                    type="date"
                                    value={date}
                                    onChange={(e) => setDate(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('customerName')} ({t('notesOptional')})</Label>
                                <Input
                                    value={customerName}
                                    onChange={(e) => setCustomerName(e.target.value)}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>{t('notes')} ({t('notesOptional')})</Label>
                                <Input
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder={t('notesPlaceholder')}
                                />
                            </div>
                        </div>

                        <div className="pt-6 flex flex-col md:flex-row items-center justify-between border-t gap-4">
                            <div className="space-y-1 text-center md:text-left">
                                <p className="text-sm text-muted-foreground">Total Returns: <span className="font-bold text-foreground">{totalQuantity}</span></p>
                                <p className="text-xl font-bold text-purple-600">
                                    Total Value: -{totalRevenue.toFixed(2)} TND
                                </p>
                            </div>
                            <Button
                                onClick={handleConfirm}
                                disabled={!isFormValid || isSubmitting}
                                className="w-full md:w-auto bg-purple-600 hover:bg-purple-700 text-white"
                                size="lg"
                            >
                                <Check className="h-5 w-5 mr-2" />
                                {t('confirm')}
                            </Button>
                        </div>

                    </CardContent>
                </Card>

                {/* Confirmation Dialog */}
                <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Confirm Stock Return</DialogTitle>
                            <DialogDescription>
                                Are you sure you want to return {totalQuantity} items to your available inventory? The recorded operation will reflect a deduction of {totalRevenue.toFixed(2)} TND from revenue.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={isSubmitting}>
                                {t('cancel')}
                            </Button>
                            <Button onClick={handleSubmit} disabled={isSubmitting} className="bg-purple-600 hover:bg-purple-700 text-white">
                                {isSubmitting ? t('loading') : "Confirm Return"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

            </div>
        </Layout>
    );
}

